import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChat } from '@/contexts/ChatContext';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';

const KEYS_BY_EVENT: Record<string, string[]> = {
  'woocommerce:product:updated': ['woo-products'],
  'woocommerce:order:updated': [
    'woo-orders',
    'woo-abandoned',
    'carts-page',
    'carts-facets',
    'orders-page',
    'orders-facets',
  ],
  'shopify:order:updated': ['shopify-orders', 'orders-page', 'orders-facets'],
  'commerce:offer:updated': ['offers'],
  // WeNext (WhatsApp) orders: address received, payment changed.
  'commerce:order:updated': ['orders-page', 'orders-facets', 'order-timeline', 'order-items'],
};

const SYNC_EVENT = 'commerce:sync:completed';

const PRODUCT_LIST_KEYS = new Set(['shopify-products', 'woo-products', 'wenext-products']);

const V2_KEYS_BY_SYNC_TYPE: Record<string, string[]> = {
  products: ['shopify-products'],
  orders: ['shopify-orders', 'orders-page', 'orders-facets'],
  checkouts: ['shopify-checkouts', 'carts-page', 'carts-facets'],
  customers: [],
  'woo-products': ['woo-products'],
  'woo-orders': [
    'woo-orders',
    'woo-abandoned',
    'carts-page',
    'carts-facets',
    'orders-page',
    'orders-facets',
  ],
  'woo-coupons': ['offers'],
  'petpooja-menu': [],
};

export type CommerceSyncEvent = {
  businessId?: string;
  workspaceId?: string;
  type?: string;
  synced?: number;
  ok?: boolean;
  error?: string;
};

function useCommerceRooms(
  workspaceId: string | null | undefined,
  businessAccountId: string | null | undefined,
) {
  const chat = useChat();
  const socket = chat?.socket ?? null;
  const isConnected = chat?.isConnected ?? false;

  useEffect(() => {
    if (!socket || !isConnected) return;
    if (workspaceId) socket.emit('join_room', { roomId: `workspace-${workspaceId}` });
    if (businessAccountId) {
      socket.emit('subscribe-business-account', businessAccountId);
    }
    // Rooms are shared and counted (lib/socketAdapter.ts): each join needs its leave,
    // or the room outlives the page and the account it was for.
    return () => {
      if (workspaceId) socket.emit('leave_room', { roomId: `workspace-${workspaceId}` });
      if (businessAccountId) socket.emit('unsubscribe-business-account', businessAccountId);
    };
  }, [socket, isConnected, workspaceId, businessAccountId]);

  return { socket, isConnected };
}

function eventIsForScope(
  data: { businessId?: string; workspaceId?: string } | undefined,
  scopeId: string | null | undefined,
  businessAccountId: string | null | undefined,
): boolean {
  if (!data?.businessId && !data?.workspaceId) return true;
  if (data.workspaceId && scopeId && data.workspaceId === scopeId) return true;
  if (data.businessId && scopeId && data.businessId === scopeId) return true;
  if (data.businessId && businessAccountId && data.businessId === businessAccountId) {
    return true;
  }
  return false;
}

function invalidateOnce(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: unknown[],
) {
  void queryClient.invalidateQueries({ queryKey }, { cancelRefetch: false });
}

export function useCommerceRealtime(selectedAccountId: string | null | undefined) {
  const { selectedWorkspace } = useWorkspaceContext();
  const businessAccountId = selectedWorkspace?.business_account_id ?? null;
  const { socket, isConnected } = useCommerceRooms(
    selectedAccountId,
    businessAccountId,
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!socket || !isConnected || !selectedAccountId) return;

    const handlers = Object.entries(KEYS_BY_EVENT).map(([event, keys]) => {
      const handler = (
        data: { businessId?: string; workspaceId?: string } | undefined,
      ) => {
        if (!eventIsForScope(data, selectedAccountId, businessAccountId)) return;
        for (const key of keys) {
          invalidateOnce(queryClient, ['commerce-v2', key, selectedAccountId]);
        }
        // The server-paged Products tab lives under ['products', ws].
        if (keys.some((k) => PRODUCT_LIST_KEYS.has(k))) {
          invalidateOnce(queryClient, ['products', selectedAccountId]);
        }
        // The Overview's headline numbers move with any order / cart / product change.
        if (keys.some((k) => k !== 'offers')) {
          invalidateOnce(queryClient, ['commerce-v2', 'overview-summary', selectedAccountId]);
        }
      };
      socket.on(event, handler);
      return [event, handler] as const;
    });

    const syncHandler = (data: CommerceSyncEvent | undefined) => {
      if (!eventIsForScope(data, selectedAccountId, businessAccountId)) return;
      const type = data?.type;
      if (!type) return;
      if (type === 'meta-review') {
        invalidateOnce(queryClient, ['products', selectedAccountId]);
        return;
      }
      if (type === 'catalog') {
        // The catalogue import finished (connecting one only QUEUES it). Re-read the
        // Products list and its badge now rather than on a guessed timer, plus the
        // catalogue's own card and the Overview's product numbers.
        invalidateOnce(queryClient, ['products', selectedAccountId]);
        invalidateOnce(queryClient, ['catalog-info']);
        invalidateOnce(queryClient, ['commerce-stats']);
        invalidateOnce(queryClient, ['commerce-v2', 'store-connections']);
        invalidateOnce(queryClient, ['commerce-v2', 'overview-summary', selectedAccountId]);
        return;
      }
      invalidateOnce(queryClient, ['commerce-v2', 'store-connections']);
      if ((V2_KEYS_BY_SYNC_TYPE[type] ?? []).some((k) => PRODUCT_LIST_KEYS.has(k))) {
        invalidateOnce(queryClient, ['products', selectedAccountId]);
      }
      for (const key of V2_KEYS_BY_SYNC_TYPE[type] ?? []) {
        invalidateOnce(queryClient, ['commerce-v2', key, selectedAccountId]);
      }
      if ((V2_KEYS_BY_SYNC_TYPE[type] ?? []).some((k) => k !== 'offers')) {
        invalidateOnce(queryClient, ['commerce-v2', 'overview-summary', selectedAccountId]);
      }
      if (type === 'petpooja-menu') {
        invalidateOnce(queryClient, ['petpooja']);
      }
    };
    socket.on(SYNC_EVENT, syncHandler);

    return () => {
      for (const [event, handler] of handlers) socket.off(event, handler);
      socket.off(SYNC_EVENT, syncHandler);
    };
  }, [socket, isConnected, selectedAccountId, businessAccountId, queryClient]);
}

export function useCommerceSyncEvents(
  selectedAccountId: string | null | undefined,
  onSynced: (event: CommerceSyncEvent) => void,
) {
  const { selectedWorkspace } = useWorkspaceContext();
  const businessAccountId = selectedWorkspace?.business_account_id ?? null;
  const { socket, isConnected } = useCommerceRooms(
    selectedAccountId,
    businessAccountId,
  );

  const onSyncedRef = useRef(onSynced);
  useEffect(() => {
    onSyncedRef.current = onSynced;
  }, [onSynced]);

  useEffect(() => {
    if (!socket || !isConnected || !selectedAccountId) return;

    const handler = (data: CommerceSyncEvent | undefined) => {
      if (!eventIsForScope(data, selectedAccountId, businessAccountId)) return;
      if (!data?.type) return;
      onSyncedRef.current(data);
    };
    socket.on(SYNC_EVENT, handler);

    return () => {
      socket.off(SYNC_EVENT, handler);
    };
  }, [socket, isConnected, selectedAccountId, businessAccountId]);
}
